import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Statistic, Typography, Space, Spin, Alert, Button } from 'antd';
import {
  AppstoreOutlined,
  TagsOutlined,
  StarOutlined,
  EyeOutlined,
  TrophyOutlined,
  RiseOutlined
} from '@ant-design/icons';
import { apiService } from '../../../services/api';
import type { AppListItem } from '../../../types';
import './Dashboard.css';

const { Title, Text } = Typography;

interface DashboardStats {
  totalApps: number;
  totalCategories: number;
  totalRatings: number;
  totalViews: number;
  popularApps: AppListItem[];
  recentApps: Array<{
    id: number;
    name: string;
    created_at: string;
    category_name: string;
  }>;
}

const Dashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // 获取统计数据
      const [appsResponse, categoriesResponse] = await Promise.all([
        apiService.getApps({ page: 1, per_page: 1 }),
        apiService.getCategories(),
      ]);

      // 获取热门应用
      const popularResponse = await apiService.getPopularApps(5);

      const dashboardStats: DashboardStats = {
        totalApps: appsResponse.data.pagination.total_items || 0,
        totalCategories: categoriesResponse.data.length || 0,
        totalRatings: 0, // 这里可以添加评分统计API
        totalViews: 0, // 这里可以添加浏览量统计API
        popularApps: popularResponse.data || [],
        recentApps: [], // 这里可以添加最近应用API
      };

      setStats(dashboardStats);
    } catch (err: any) {
      setError('加载数据失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <Spin size="large" />
        <Text style={{ marginTop: 16, display: 'block', textAlign: 'center' }}>
          加载中...
        </Text>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <Alert
          message="加载失败"
          description={error}
          type="error"
          showIcon
          action={
            <Button onClick={loadDashboardData} type="primary" size="small">
              重试
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <Title level={2}>仪表盘</Title>
        <Text type="secondary">欢迎回来，这里是系统概览</Text>
      </div>

      {/* 统计卡片 */}
      <Row gutter={[24, 24]} className="stats-row">
        <Col xs={24} sm={12} lg={6}>
          <Card className="stat-card">
            <Statistic
              title="应用总数"
              value={stats?.totalApps || 0}
              prefix={<AppstoreOutlined className="stat-icon apps" />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="stat-card">
            <Statistic
              title="分类总数"
              value={stats?.totalCategories || 0}
              prefix={<TagsOutlined className="stat-icon categories" />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="stat-card">
            <Statistic
              title="评分总数"
              value={stats?.totalRatings || 0}
              prefix={<StarOutlined className="stat-icon ratings" />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="stat-card">
            <Statistic
              title="浏览总数"
              value={stats?.totalViews || 0}
              prefix={<EyeOutlined className="stat-icon views" />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 热门应用和最近应用 */}
      <Row gutter={[24, 24]} className="content-row">
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <TrophyOutlined />
                热门应用
              </Space>
            }
            className="content-card"
          >
            <div className="popular-apps">
              {stats?.popularApps?.length ? (
                stats.popularApps.map((app, index) => (
                  <div key={app.id} className="popular-app-item">
                    <div className="app-rank">#{index + 1}</div>
                    <div className="app-info">
                      <div className="app-name">{app.name}</div>
                      <div className="app-stats">
                        <Space size="large">
                          <span>
                            <EyeOutlined /> {app.click_count || 0} 次查看
                          </span>
                          <span className="category">
                            {app.category.name}
                          </span>
                        </Space>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-state">
                  <Text type="secondary">暂无数据</Text>
                </div>
              )}
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <RiseOutlined />
                最近添加
              </Space>
            }
            className="content-card"
          >
            <div className="recent-apps">
              {stats?.recentApps?.length ? (
                stats.recentApps.map((app) => (
                  <div key={app.id} className="recent-app-item">
                    <div className="app-info">
                      <div className="app-name">{app.name}</div>
                      <div className="app-meta">
                        <Text type="secondary">{app.category_name}</Text>
                        <Text type="secondary">{app.created_at}</Text>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-state">
                  <Text type="secondary">暂无数据</Text>
                </div>
              )}
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
